import { MathUtils, Vector2, Vector3 } from 'three';
import { DEG2RAD } from 'three/src/math/MathUtils.js';

import { LineSegment } from '@/modules/common/types/general';

export const cross = (a: Vector2, b: Vector2): number => a.x * b.y - a.y * b.x;

export const rotateVector = (v: Vector2, angleDegree: number): Vector2 => {
  const angle = angleDegree * DEG2RAD;
  return new Vector2(
    v.x * Math.cos(angle) - v.y * Math.sin(angle),
    v.x * Math.sin(angle) + v.y * Math.cos(angle),
  );
};

export function rotatePoint(
  x: number,
  y: number,
  cx: number,
  cy: number,
  angleInRad: number,
): { x: number; y: number } {
  const cos = Math.cos(angleInRad);
  const sin = Math.sin(angleInRad);
  const dx = x - cx;
  const dy = y - cy;

  return {
    x: cx + dx * cos - dy * sin,
    y: cy + dx * sin + dy * cos,
  };
}

export const getVectorRotatedAroundPoint = (
  vectorToRotate: Vector2,
  centerOfRotation: Vector2,
  angleInDegrees: number,
): Vector2 => {
  // TODO:
  // NOTE: reuse rotateAround method from three.js

  const angleInRadians = MathUtils.degToRad(angleInDegrees);
  const c = Math.cos(angleInRadians);
  const s = Math.sin(angleInRadians);

  const x = vectorToRotate.x - centerOfRotation.x;
  const y = vectorToRotate.y - centerOfRotation.y;

  const newX = x * c - y * s + centerOfRotation.x;
  const newY = x * s + y * c + centerOfRotation.y;

  return vectorToRotate.clone().setX(newX).setY(newY);
};

export const getMiddlePoint = (p1: Vector2, p2: Vector2): Vector2 =>
  new Vector2().lerpVectors(p1, p2, 0.5);

export const centerOfSize = (size: { width: number; height: number }): Vector2 =>
  new Vector2(size.width / 2, size.height / 2);

/** @description Modulo operation that handles negative numbers correctly
 * example: mod(-1, 5) = 4
 */
export const mod = (n: number, m: number) => ((n % m) + m) % m;

/**
 * Clamps the angle within a 0 - 180 and -179 - 0 range
 */
export const clampAngle180 = (angle: number) => {
  const modAngle = mod(angle, 360);
  return modAngle > 180 ? modAngle - 360 : modAngle;
};

export const convertV2ToV3 = (vector: Vector2, z = 0): Vector3 =>
  new Vector3(vector.x, vector.y, z);

/**
 * @description Rotates a point (x, y) around another point (cx, cy) by a given angle in radians.
 *
 * Rotation direction: positive x-axis to positive y-axis.
 */
export function getRotatedPoint(
  x: number,
  y: number,
  cx: number,
  cy: number,
  angle: number,
): { x: number; y: number } {
  // normalize / translate point to origin
  const translatedX = x - cx;
  const translatedY = y - cy;

  // perform rotation
  const rotatedX = translatedX * Math.cos(angle) - translatedY * Math.sin(angle);
  const rotatedY = translatedX * Math.sin(angle) + translatedY * Math.cos(angle);

  // translate point back
  const newX = rotatedX + cx;
  const newY = rotatedY + cy;

  return {
    x: newX,
    y: newY,
  };
}

export function findNearestPointOnLine(point: Vector2, line: LineSegment): Vector2 {
  const atob = { x: line.end.x - line.start.x, y: line.end.y - line.start.y };
  const atop = { x: point.x - line.start.x, y: point.y - line.start.y };
  const len = atob.x * atob.x + atob.y * atob.y;
  const dot = atop.x * atob.x + atop.y * atob.y;
  const t = Math.min(1, Math.max(0, dot / len));
  return new Vector2(line.start.x + atob.x * t, line.start.y + atob.y * t);
}

export function getPointProjectionParameterOnLine(point: Vector2, line: LineSegment): number {
  const atob = { x: line.end.x - line.start.x, y: line.end.y - line.start.y };
  const atop = { x: point.x - line.start.x, y: point.y - line.start.y };
  const len = atob.x * atob.x + atob.y * atob.y;

  if (len === 0) return 0;

  return (atop.x * atob.x + atop.y * atob.y) / len;
}

export function isPointProjectionBetweenLineEndpoints(
  point: Vector2,
  line: LineSegment,
  epsilon = 1e-6,
): boolean {
  const t = getPointProjectionParameterOnLine(point, line);

  return t > epsilon && t < 1 - epsilon;
}

export function roundToDecimals(value: number, decimals = 3): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/**
 * Returns the nearest point pair between two polygon boundaries — one point
 * on each polygon's edges.  Checks all endpoint-to-edge projections, which is
 * sufficient for convex polygons (rectangles, segments).
 */
export function findClosestPointsBetweenPolygons(
  polyA: Vector2[],
  polyB: Vector2[],
): { pointOnA: Vector2; pointOnB: Vector2 } {
  const edgesOf = (poly: Vector2[]): LineSegment[] =>
    poly.map((p, i) => ({ start: p, end: poly[(i + 1) % poly.length] }));

  const edgesA = edgesOf(polyA);
  const edgesB = edgesOf(polyB);

  let minDist = Infinity;
  let bestA = polyA[0];
  let bestB = polyB[0];

  const tryPair = (pA: Vector2, pB: Vector2) => {
    const d = pA.distanceTo(pB);
    if (d < minDist) {
      minDist = d;
      bestA = pA;
      bestB = pB;
    }
  };

  edgesA.forEach((edgeA) => {
    edgesB.forEach((edgeB) => {
      tryPair(edgeA.start, findNearestPointOnLine(edgeA.start, edgeB));
      tryPair(edgeA.end, findNearestPointOnLine(edgeA.end, edgeB));
      tryPair(findNearestPointOnLine(edgeB.start, edgeA), edgeB.start);
      tryPair(findNearestPointOnLine(edgeB.end, edgeA), edgeB.end);
    });
  });

  return { pointOnA: bestA, pointOnB: bestB };
}

/**
 * Returns the smallest angular difference in degrees between two angles given in radians.
 * Result is always in the range [0, 90] since highways are treated as undirected (mod 180).
 */
export function angularDifferenceDeg(a: number, b: number): number {
  const RAD2DEG = 180 / Math.PI;
  const diff = Math.abs(a * RAD2DEG - b * RAD2DEG) % 180;
  return Math.min(diff, 180 - diff);
}
